import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";

// -----------------------------------------------------------------
// Public, customer-facing short version of the Risikoanalyse form.
// Sent via URL/token; the data feeds back into the internal long form.
// -----------------------------------------------------------------

const BRAND = {
  fontPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  pageBg: "#361936",
  purple: "#4F266B",
  accent: "#F8AA00",
  cardBg: "#fbf9fc",
  ink: "#1F1B24",
  muted: "#7c7480",
  borderDashed: "#e3dde6",
};

const useStyles = makeStyles({
  page: {
    minHeight: ["100vh", "100dvh"],
    backgroundColor: BRAND.pageBg,
    backgroundImage:
      "radial-gradient(120% 120% at 85% 15%, #4F266B 0%, #3f1f57 28%, #361936 62%, #361936 100%)",
    color: "#fff",
    fontFamily: BRAND.fontPrimary,
    paddingBottom: "60px",
  },
  hero: {
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "48px",
    paddingBottom: "20px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  eyebrow: {
    fontSize: "0.78rem",
    color: BRAND.accent,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.8rem, 5vw, 3rem)",
    lineHeight: 1.1,
    color: BRAND.accent,
    marginTop: "8px",
    marginBottom: "8px",
    fontWeight: 400,
  },
  intro: {
    color: "#d6cfde",
    fontSize: "1rem",
    maxWidth: "640px",
    lineHeight: 1.5,
  },
  pillars: {
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "16px",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  pillarChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "12px",
    backgroundColor: "rgba(255,255,255,0.06)",
    color: "#fff",
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.18)"),
    ...shorthands.borderRadius("999px"),
  },
  pillarDot: {
    width: "8px",
    height: "8px",
    ...shorthands.borderRadius("999px"),
  },
  card: {
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: "18px",
    backgroundColor: BRAND.cardBg,
    color: BRAND.ink,
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "14px",
  },
  sectionNumber: {
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.purple,
    color: "#fff",
    fontFamily: BRAND.fontAccent,
    fontWeight: 600,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  sectionTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.ink,
    margin: 0,
  },
  sectionDesc: {
    color: BRAND.muted,
    fontSize: "0.9rem",
    marginTop: "4px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": { gridTemplateColumns: "1fr 1fr" },
  },
  gridFull: { gridColumn: "1 / -1" },
  field: { display: "flex", flexDirection: "column", rowGap: "4px" },
  label: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  input: {
    boxSizing: "border-box",
    width: "100%",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: "#fff",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("8px"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  textarea: { minHeight: "100px", resize: "vertical" },
  yesNoRow: {
    display: "inline-flex",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("8px"),
  },
  yesNoBtn: {
    appearance: "none",
    backgroundColor: "#fff",
    color: BRAND.muted,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "16px",
    paddingRight: "16px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    ...shorthands.border("none"),
  },
  yesNoActiveYes: {
    backgroundColor: "#e7f6ec",
    color: "#0e7c3a",
    fontWeight: 700,
  },
  yesNoActiveNo: {
    backgroundColor: "#fdecec",
    color: "#b91c1c",
    fontWeight: 700,
  },
  saveCard: {
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.ink,
    backgroundColor: BRAND.accent,
    backgroundImage: "none",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "28px",
    paddingRight: "28px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    boxShadow: "0 8px 20px rgba(248,170,0,0.4)",
    ":hover": { backgroundColor: "#fbc14b" },
    ":disabled": {
      backgroundColor: "#666",
      color: "#ddd",
      cursor: "not-allowed",
      boxShadow: "none",
    },
  },
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: "#0e7c3a",
    color: "#fff",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "20px",
    paddingRight: "20px",
    fontSize: "0.95rem",
    ...shorthands.borderRadius("8px"),
    boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
    zIndex: 1000,
  },
  toastError: { backgroundColor: "#b91c1c" },
  loading: {
    color: "#d6cfde",
    textAlign: "center",
    paddingTop: "60px",
    fontSize: "1.1rem",
  },
  thanks: {
    maxWidth: "640px",
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: BRAND.cardBg,
    color: BRAND.ink,
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "28px",
    paddingRight: "28px",
    ...shorthands.borderRadius("14px"),
    textAlign: "center",
    marginTop: "60px",
  },
});

const PILLARS: Array<{ key: 1 | 2 | 3 | 4; label: string; color: string }> = [
  { key: 1, label: "Sachwerte", color: "#E66A2C" },
  { key: 2, label: "Haftung", color: "#3D8BD9" },
  { key: 3, label: "Menschen", color: "#2E7D4F" },
  { key: 4, label: "Liquidität", color: "#1F1B24" },
];

// ---------- Form data shape -----------------------------------------------

type FormData = {
  // 1. Allgemein
  gruendungsdatum?: string;
  gruender?: string;
  rechtsform?: string;
  hrbNummer?: string;
  hrbRegister?: string;
  steuernummer?: string;
  ustNummer?: string;
  finanzamt?: string;
  vorsteuerabzug?: "ja" | "nein" | "";
  geschaeftsjahrKalender?: "ja" | "nein" | "";
  geschaeftsjahrAbweichend?: string;
  beteiligungenJa?: "ja" | "nein" | "";
  beteiligungenDetail?: string;
  niederlassungenJa?: "ja" | "nein" | "";
  niederlassungenDetail?: string;
  rechtsvorgaengerJa?: "ja" | "nein" | "";
  rechtsvorgaengerDetail?: string;
  bilanzpflicht?: "ja" | "nein" | "";
  pruefpflicht?: "ja" | "nein" | "";
  pruefer?: string;

  // 2. Personen
  geschaeftsfuehrer?: string;
  gesellschafter?: string;

  // 3. Verantwortlichkeiten (Externe)
  steuerberater?: string;
  rechtsanwalt?: string;
  bank?: string;
  notar?: string;
  itDienstleister?: string;
  sicherheitsbeauftragter?: string;
  datenschutz?: string;
  qualitaetsmgmt?: string;

  // 4. Tätigkeit
  taetigkeit?: string;

  // 5. Selbsteinschätzung Risiken
  haftungsrisiko?: "ja" | "nein" | "";
  haftungsrisikoDetail?: string;
  finanzrisiko?: "ja" | "nein" | "";
  finanzrisikoDetail?: string;
  rechtsrisiko?: "ja" | "nein" | "";
  rechtsrisikoDetail?: string;
  cyberrisiko?: "ja" | "nein" | "";
  cyberrisikoDetail?: string;
  umweltrisiko?: "ja" | "nein" | "";
  umweltrisikoDetail?: string;

  // 6. Standorte
  standort1?: string;
  standort1Eigentum?: "ja" | "nein" | "";
  standort1Flaeche?: string;
  standort2?: string;
  standort2Eigentum?: "ja" | "nein" | "";
  standort2Flaeche?: string;

  // 7. Vermögenswerte
  vmKaufmaennisch?: string;
  vmTechnisch?: string;
  vmWaren?: string;
  vmGrundstuecke?: string;
  vmFahrzeugeEigene?: string;
  vmFahrzeugeFremde?: string;
  vmSonstiges?: string;

  // 8. Transport
  transportJa?: "ja" | "nein" | "";
  transportDetail?: string;

  // 9. Kennzahlen kurz
  umsatzVorletzt?: string;
  umsatzLetzt?: string;
  umsatzLaufend?: string;
  umsatzPlanung?: string;
  wareneinsatzVorletzt?: string;
  wareneinsatzLetzt?: string;
  wareneinsatzLaufend?: string;
  wareneinsatzPlanung?: string;
  rohertragVorletzt?: string;
  rohertragLetzt?: string;
  rohertragLaufend?: string;
  rohertragPlanung?: string;
  umsatzrendite?: string;

  // 10. Personal
  mitarbeiterGesamt?: string;
  mitarbeiterGleichzeitig?: string;
  bgLohnsumme?: string;
  leiharbeiterJa?: "ja" | "nein" | "";
  leiharbeiterAnzahl?: string;
  subunternehmerJa?: "ja" | "nein" | "";
  subunternehmerAnzahl?: string;
  freelancerJa?: "ja" | "nein" | "";
  freelancerAnzahl?: string;
  besonderesPersonal?: string;
  versorgungGF?: string;

  // 11. Erwartung & Notizen
  erwartung?: string;
  besonderheiten?: string;
  zufriedenheitMakler?: string;
  notizenWeg?: string;
};

// ---------- Component -----------------------------------------------------

const PublicCustomerRisikoanalyseKurzScrollForm = () => {
  const styles = useStyles();
  const { token } = useParams();

  const [data, setData] = useState<FormData>({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) throw new Error("Formular nicht gefunden");
        const payload = await res.json();
        if (aborted) return;
        if (payload?.status === "FILLED") {
          setSubmitted(true);
        } else if (payload?.data) {
          try {
            const parsed = typeof payload.data === "string" ? JSON.parse(payload.data) : payload.data;
            if (parsed && typeof parsed === "object") setData(parsed as FormData);
          } catch {
            // ignore
          }
        }
      } catch (err) {
        setError("Formular konnte nicht geladen werden. Prüfen Sie den Link.");
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [token]);

  const set = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setData((prev) => ({ ...prev, [k]: v }));

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSubmit = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "FILLED", data: JSON.stringify(data) }),
      });
      if (!res.ok) throw new Error("Speichern fehlgeschlagen");
      showToast("ok", "Vielen Dank! Ihre Angaben wurden übermittelt.");
      setSubmitted(true);
    } catch {
      showToast("err", "Speichern fehlgeschlagen — bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Formular wird geladen…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.thanks}>
          <h2 style={{ fontFamily: BRAND.fontAccent, color: BRAND.purple }}>Hinweis</h2>
          <p style={{ color: BRAND.muted }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.page}>
        <div className={styles.thanks}>
          <h2 style={{ fontFamily: BRAND.fontAccent, color: BRAND.purple, marginTop: 0 }}>
            Vielen Dank!
          </h2>
          <p style={{ color: BRAND.muted, fontSize: "1rem" }}>
            Ihre Angaben wurden erfolgreich übermittelt. Ihr Berater meldet sich
            in Kürze bei Ihnen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.eyebrow}>Risikoanalyse · Kurzversion</div>
        <h1 className={styles.title}>Lernen wir Ihr Unternehmen kennen.</h1>
        <p className={styles.intro}>
          Bitte beantworten Sie die folgenden Fragen so vollständig wie möglich.
          Ihre Angaben helfen uns, Ihre tatsächlichen Risiken zu verstehen — und
          den passenden Versicherungsschutz mit Ihnen zu erarbeiten.
        </p>
      </div>

      <div className={styles.pillars}>
        {PILLARS.map((p) => (
          <span key={p.key} className={styles.pillarChip}>
            <span className={styles.pillarDot} style={{ backgroundColor: p.color }} />
            {p.key}. {p.label}
          </span>
        ))}
      </div>

      {/* Section 1: Allgemein */}
      <Section
        styles={styles}
        n={1}
        title="Allgemeines zum Unternehmen"
        desc="Gründung, Rechtsform, Register"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Gründungsdatum">
            <input
              type="date"
              className={styles.input}
              value={data.gruendungsdatum ?? ""}
              onChange={(e) => set("gruendungsdatum", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Gründer (Name)">
            <input
              className={styles.input}
              value={data.gruender ?? ""}
              onChange={(e) => set("gruender", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Rechtsform">
            <input
              className={styles.input}
              value={data.rechtsform ?? ""}
              onChange={(e) => set("rechtsform", e.target.value)}
              placeholder="GmbH, AG, GbR …"
            />
          </Field>
          <Field styles={styles} label="HRB-Nummer">
            <input
              className={styles.input}
              value={data.hrbNummer ?? ""}
              onChange={(e) => set("hrbNummer", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Register (Amtsgericht)">
            <input
              className={styles.input}
              value={data.hrbRegister ?? ""}
              onChange={(e) => set("hrbRegister", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Steuernummer">
            <input
              className={styles.input}
              value={data.steuernummer ?? ""}
              onChange={(e) => set("steuernummer", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="USt-IdNr.">
            <input
              className={styles.input}
              value={data.ustNummer ?? ""}
              onChange={(e) => set("ustNummer", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Zuständiges Finanzamt">
            <input
              className={styles.input}
              value={data.finanzamt ?? ""}
              onChange={(e) => set("finanzamt", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Vorsteuerabzug?">
            <YesNo styles={styles} value={data.vorsteuerabzug ?? ""} onChange={(v) => set("vorsteuerabzug", v)} />
          </Field>
          <Field styles={styles} label="Geschäftsjahr = Kalenderjahr?">
            <YesNo
              styles={styles}
              value={data.geschaeftsjahrKalender ?? ""}
              onChange={(v) => set("geschaeftsjahrKalender", v)}
            />
            {data.geschaeftsjahrKalender === "nein" && (
              <input
                className={styles.input}
                style={{ marginTop: 6 }}
                placeholder="abweichend (Monat/Tag)"
                value={data.geschaeftsjahrAbweichend ?? ""}
                onChange={(e) => set("geschaeftsjahrAbweichend", e.target.value)}
              />
            )}
          </Field>
          <Field styles={styles} label="Beteiligungen an anderen Unternehmen?">
            <YesNo
              styles={styles}
              value={data.beteiligungenJa ?? ""}
              onChange={(v) => set("beteiligungenJa", v)}
            />
            {data.beteiligungenJa === "ja" && (
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="An welchen, mit welcher Quote, was machen die Unternehmen?"
                value={data.beteiligungenDetail ?? ""}
                onChange={(e) => set("beteiligungenDetail", e.target.value)}
              />
            )}
          </Field>
          <Field styles={styles} label="Niederlassungen?">
            <YesNo
              styles={styles}
              value={data.niederlassungenJa ?? ""}
              onChange={(v) => set("niederlassungenJa", v)}
            />
            {data.niederlassungenJa === "ja" && (
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="Welche, wo, was machen sie?"
                value={data.niederlassungenDetail ?? ""}
                onChange={(e) => set("niederlassungenDetail", e.target.value)}
              />
            )}
          </Field>
          <Field styles={styles} label="Rechtsvorgänger?">
            <YesNo
              styles={styles}
              value={data.rechtsvorgaengerJa ?? ""}
              onChange={(v) => set("rechtsvorgaengerJa", v)}
            />
            {data.rechtsvorgaengerJa === "ja" && (
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="Welcher, wann, wieso verändert?"
                value={data.rechtsvorgaengerDetail ?? ""}
                onChange={(e) => set("rechtsvorgaengerDetail", e.target.value)}
              />
            )}
          </Field>
          <Field styles={styles} label="Bilanzpflichtig?">
            <YesNo styles={styles} value={data.bilanzpflicht ?? ""} onChange={(v) => set("bilanzpflicht", v)} />
            {data.bilanzpflicht === "ja" && (
              <>
                <YesNo
                  styles={styles}
                  value={data.pruefpflicht ?? ""}
                  onChange={(v) => set("pruefpflicht", v)}
                />
                <span style={{ fontSize: "0.78rem", color: BRAND.muted }}>
                  …auch der Prüfungspflicht (§ 316 HGB)?
                </span>
                {data.pruefpflicht === "ja" && (
                  <input
                    className={styles.input}
                    placeholder="Wirtschafts-/Buchprüfer (Name, Anschrift)"
                    value={data.pruefer ?? ""}
                    onChange={(e) => set("pruefer", e.target.value)}
                  />
                )}
              </>
            )}
          </Field>
        </div>
      </Section>

      {/* Section 2: Personen */}
      <Section
        styles={styles}
        n={2}
        title="Gesellschafter & Geschäftsführer"
        desc="Bitte mit Name, Geburtsdatum, Anteil"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Geschäftsführer" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.geschaeftsfuehrer ?? ""}
              onChange={(e) => set("geschaeftsfuehrer", e.target.value)}
              placeholder="Name, Vorname, Geburtsdatum, Kontakt — pro Zeile eine Person"
            />
          </Field>
          <Field styles={styles} label="Gesellschafter (mit Anteilen)" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.gesellschafter ?? ""}
              onChange={(e) => set("gesellschafter", e.target.value)}
              placeholder="Name oder Firma, Anteil in %, ggf. Sitz"
            />
          </Field>
        </div>
      </Section>

      {/* Section 3: Externe Verantwortlichkeiten */}
      <Section
        styles={styles}
        n={3}
        title="Externe Verantwortlichkeiten"
        desc="Wer betreut Ihr Unternehmen extern?"
      >
        <div className={styles.grid}>
          {(
            [
              ["steuerberater", "Steuerberater"],
              ["rechtsanwalt", "Rechtsanwalt"],
              ["bank", "Bank / Hausbank"],
              ["notar", "Notar"],
              ["itDienstleister", "IT-Dienstleister"],
              ["sicherheitsbeauftragter", "Sicherheitsbeauftragter"],
              ["datenschutz", "Datenschutzbeauftragter"],
              ["qualitaetsmgmt", "Qualitätsmanagement (z.B. ISO 9001)"],
            ] as Array<[keyof FormData, string]>
          ).map(([k, lbl]) => (
            <Field key={k} styles={styles} label={lbl}>
              <input
                className={styles.input}
                value={(data[k] as string) ?? ""}
                onChange={(e) => set(k, e.target.value as any)}
                placeholder="Name, Firma, Kontakt"
              />
            </Field>
          ))}
        </div>
      </Section>

      {/* Section 4: Tätigkeit */}
      <Section
        styles={styles}
        n={4}
        title="Was macht Ihr Unternehmen?"
        desc="Bitte in eigenen Worten den Betriebscharakter beschreiben."
      >
        <textarea
          className={mergeClasses(styles.input, styles.textarea)}
          value={data.taetigkeit ?? ""}
          onChange={(e) => set("taetigkeit", e.target.value)}
          placeholder="Tätigkeit, Produkte, Zielgruppe, Besonderheiten…"
          style={{ minHeight: 140 }}
        />
      </Section>

      {/* Section 5: Selbsteinschätzung Risiken */}
      <Section
        styles={styles}
        n={5}
        title="Selbsteinschätzung Risiken"
        desc="Wo sehen Sie selbst Risiken?"
      >
        <div className={styles.grid}>
          <RiskBlock
            styles={styles}
            label="Haftungsrisiken"
            yn={data.haftungsrisiko ?? ""}
            onYn={(v) => set("haftungsrisiko", v)}
            detail={data.haftungsrisikoDetail ?? ""}
            onDetail={(v) => set("haftungsrisikoDetail", v)}
          />
          <RiskBlock
            styles={styles}
            label="Ausfall-, Finanz- und Liquiditätsrisiken"
            yn={data.finanzrisiko ?? ""}
            onYn={(v) => set("finanzrisiko", v)}
            detail={data.finanzrisikoDetail ?? ""}
            onDetail={(v) => set("finanzrisikoDetail", v)}
          />
          <RiskBlock
            styles={styles}
            label="Risiken aus Rechtsstreitigkeiten"
            yn={data.rechtsrisiko ?? ""}
            onYn={(v) => set("rechtsrisiko", v)}
            detail={data.rechtsrisikoDetail ?? ""}
            onDetail={(v) => set("rechtsrisikoDetail", v)}
          />
          <RiskBlock
            styles={styles}
            label="Cyber-Risiken"
            yn={data.cyberrisiko ?? ""}
            onYn={(v) => set("cyberrisiko", v)}
            detail={data.cyberrisikoDetail ?? ""}
            onDetail={(v) => set("cyberrisikoDetail", v)}
          />
          <RiskBlock
            styles={styles}
            label="Umweltrisiken (Öltank, Notstrom, Abscheider, Maschineninhalte, Brennb. Gase ≥ 3 t)"
            yn={data.umweltrisiko ?? ""}
            onYn={(v) => set("umweltrisiko", v)}
            detail={data.umweltrisikoDetail ?? ""}
            onDetail={(v) => set("umweltrisikoDetail", v)}
          />
        </div>
      </Section>

      {/* Section 6: Standorte */}
      <Section
        styles={styles}
        n={6}
        title="Betriebsstätten / Standorte"
        desc="Wo befinden sich Ihre Betriebsstätten oder Lager?"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Standort 1 (Adresse)" full>
            <input
              className={styles.input}
              value={data.standort1 ?? ""}
              onChange={(e) => set("standort1", e.target.value)}
              placeholder="Straße HsNr., PLZ, Ort"
            />
          </Field>
          <Field styles={styles} label="Eigentum?">
            <YesNo styles={styles} value={data.standort1Eigentum ?? ""} onChange={(v) => set("standort1Eigentum", v)} />
          </Field>
          <Field styles={styles} label="Fläche (gesamt / selbst genutzt)">
            <input
              className={styles.input}
              value={data.standort1Flaeche ?? ""}
              onChange={(e) => set("standort1Flaeche", e.target.value)}
              placeholder="z.B. 1250 m² / 800 m²"
            />
          </Field>
          <Field styles={styles} label="Standort 2 (optional)" full>
            <input
              className={styles.input}
              value={data.standort2 ?? ""}
              onChange={(e) => set("standort2", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Eigentum?">
            <YesNo styles={styles} value={data.standort2Eigentum ?? ""} onChange={(v) => set("standort2Eigentum", v)} />
          </Field>
          <Field styles={styles} label="Fläche (gesamt / selbst genutzt)">
            <input
              className={styles.input}
              value={data.standort2Flaeche ?? ""}
              onChange={(e) => set("standort2Flaeche", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      {/* Section 7: Vermögenswerte */}
      <Section
        styles={styles}
        n={7}
        title="Vermögenswerte"
        desc="Bitte Neuwerte angeben, wo möglich."
      >
        <div className={styles.grid}>
          {(
            [
              ["vmKaufmaennisch", "Kaufm. Betriebseinrichtung"],
              ["vmTechnisch", "Technische Betriebseinrichtung"],
              ["vmWaren", "Waren / Vorräte"],
              ["vmGrundstuecke", "Grundstücke"],
              ["vmFahrzeugeEigene", "Eigene Fahrzeuge (Anzahl/Beschreibung)"],
              ["vmFahrzeugeFremde", "Fremde Fahrzeuge (Anzahl)"],
              ["vmSonstiges", "Sonstiges"],
            ] as Array<[keyof FormData, string]>
          ).map(([k, lbl]) => (
            <Field key={k} styles={styles} label={lbl} full={k === "vmSonstiges"}>
              <input
                className={styles.input}
                value={(data[k] as string) ?? ""}
                onChange={(e) => set(k, e.target.value as any)}
                placeholder="Was, wo, in € (Neuwert)"
              />
            </Field>
          ))}
        </div>
      </Section>

      {/* Section 8: Transport */}
      <Section
        styles={styles}
        n={8}
        title="Transport"
        desc="Versenden / erhalten Sie Waren oder Betriebsmittel?"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Transportbedarf?" full>
            <YesNo styles={styles} value={data.transportJa ?? ""} onChange={(v) => set("transportJa", v)} />
            {data.transportJa === "ja" && (
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="Wie? Eigene LKW, Spediteur, Werkverkehr…"
                value={data.transportDetail ?? ""}
                onChange={(e) => set("transportDetail", e.target.value)}
              />
            )}
          </Field>
        </div>
      </Section>

      {/* Section 9: Kennzahlen kurz */}
      <Section
        styles={styles}
        n={9}
        title="Kennzahlen (Kurzform)"
        desc="Vorletztes / Letztes / Laufendes Jahr / Planung folgendes"
      >
        <KennzahlenBlock styles={styles} label="Umsatz (€)" base="umsatz" data={data} set={set} />
        <KennzahlenBlock styles={styles} label="Wareneinsatz (€)" base="wareneinsatz" data={data} set={set} />
        <KennzahlenBlock styles={styles} label="Rohertrag (€)" base="rohertrag" data={data} set={set} />
        <div className={styles.grid} style={{ marginTop: 12 }}>
          <Field styles={styles} label="Umsatzrendite (%)">
            <input
              className={styles.input}
              value={data.umsatzrendite ?? ""}
              onChange={(e) => set("umsatzrendite", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      {/* Section 10: Personal */}
      <Section
        styles={styles}
        n={10}
        title="Menschen im Unternehmen"
        desc="Mitarbeiter, externe Kräfte, Versorgungen"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Mitarbeiter gesamt">
            <input
              className={styles.input}
              value={data.mitarbeiterGesamt ?? ""}
              onChange={(e) => set("mitarbeiterGesamt", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Mitarbeiter zur gleichen Zeit">
            <input
              className={styles.input}
              value={data.mitarbeiterGleichzeitig ?? ""}
              onChange={(e) => set("mitarbeiterGleichzeitig", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Brutto-Lohn-/Gehaltssumme nach BG-Meldung (€)" full>
            <input
              className={styles.input}
              value={data.bgLohnsumme ?? ""}
              onChange={(e) => set("bgLohnsumme", e.target.value)}
            />
          </Field>
          <ExternBlock
            styles={styles}
            label="Leiharbeiter?"
            ynKey="leiharbeiterJa"
            countKey="leiharbeiterAnzahl"
            data={data}
            set={set}
          />
          <ExternBlock
            styles={styles}
            label="Subunternehmer?"
            ynKey="subunternehmerJa"
            countKey="subunternehmerAnzahl"
            data={data}
            set={set}
          />
          <ExternBlock
            styles={styles}
            label="Freelancer?"
            ynKey="freelancerJa"
            countKey="freelancerAnzahl"
            data={data}
            set={set}
          />
          <Field styles={styles} label="Besonderes für Mitarbeiter (z.B. bAV, Zuschüsse)" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.besonderesPersonal ?? ""}
              onChange={(e) => set("besonderesPersonal", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Versorgung Geschäftsführer / Gesellschafter" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.versorgungGF ?? ""}
              onChange={(e) => set("versorgungGF", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      {/* Section 11: Erwartung */}
      <Section
        styles={styles}
        n={11}
        title="Was erwarten Sie von uns?"
        desc="Erwartungshaltung, Besonderheiten, Zufriedenheit"
      >
        <div className={styles.grid}>
          <Field styles={styles} label="Erwartung an den Berater" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.erwartung ?? ""}
              onChange={(e) => set("erwartung", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Besonderheiten bei Verträgen / Risiken / Schäden / SB" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.besonderheiten ?? ""}
              onChange={(e) => set("besonderheiten", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Wie zufrieden waren Sie bisher mit Ihrem Makler / Berater?" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.zufriedenheitMakler ?? ""}
              onChange={(e) => set("zufriedenheitMakler", e.target.value)}
            />
          </Field>
          <Field styles={styles} label="Was möchten Sie uns noch mit auf den Weg geben?" full>
            <textarea
              className={mergeClasses(styles.input, styles.textarea)}
              value={data.notizenWeg ?? ""}
              onChange={(e) => set("notizenWeg", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <div className={styles.saveCard}>
        <button className={styles.primaryBtn} onClick={handleSubmit} disabled={saving}>
          {saving ? "Wird übermittelt…" : "Angaben übermitteln →"}
        </button>
      </div>

      {toast && (
        <div className={mergeClasses(styles.toast, toast.kind === "err" && styles.toastError)}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

// ---------- Helper components --------------------------------------------

const Section: React.FC<{
  styles: ReturnType<typeof useStyles>;
  n: number;
  title: string;
  desc?: string;
  children: React.ReactNode;
}> = ({ styles, n, title, desc, children }) => (
  <div className={styles.card}>
    <div className={styles.cardHead}>
      <span className={styles.sectionNumber}>{n}</span>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {desc && <div className={styles.sectionDesc}>{desc}</div>}
      </div>
    </div>
    {children}
  </div>
);

const Field: React.FC<{
  styles: ReturnType<typeof useStyles>;
  label: string;
  full?: boolean;
  children: React.ReactNode;
}> = ({ styles, label, full, children }) => (
  <div className={mergeClasses(styles.field, full && styles.gridFull)}>
    <label className={styles.label}>{label}</label>
    {children}
  </div>
);

const YesNo: React.FC<{
  styles: ReturnType<typeof useStyles>;
  value: "ja" | "nein" | "";
  onChange: (v: "ja" | "nein" | "") => void;
}> = ({ styles, value, onChange }) => (
  <div className={styles.yesNoRow} role="radiogroup">
    <button
      type="button"
      role="radio"
      aria-checked={value === "ja"}
      className={mergeClasses(styles.yesNoBtn, value === "ja" && styles.yesNoActiveYes)}
      onClick={() => onChange(value === "ja" ? "" : "ja")}
    >
      ja
    </button>
    <button
      type="button"
      role="radio"
      aria-checked={value === "nein"}
      className={mergeClasses(styles.yesNoBtn, value === "nein" && styles.yesNoActiveNo)}
      onClick={() => onChange(value === "nein" ? "" : "nein")}
    >
      nein
    </button>
  </div>
);

const RiskBlock: React.FC<{
  styles: ReturnType<typeof useStyles>;
  label: string;
  yn: "ja" | "nein" | "";
  onYn: (v: "ja" | "nein" | "") => void;
  detail: string;
  onDetail: (v: string) => void;
}> = ({ styles, label, yn, onYn, detail, onDetail }) => (
  <Field styles={styles} label={label} full>
    <YesNo styles={styles} value={yn} onChange={onYn} />
    {yn === "ja" && (
      <textarea
        className={mergeClasses(styles.input, styles.textarea)}
        style={{ marginTop: 6 }}
        placeholder="Bitte beschreiben Sie die Risiken näher…"
        value={detail}
        onChange={(e) => onDetail(e.target.value)}
      />
    )}
  </Field>
);

const KennzahlenBlock: React.FC<{
  styles: ReturnType<typeof useStyles>;
  label: string;
  base: "umsatz" | "wareneinsatz" | "rohertrag";
  data: FormData;
  set: <K extends keyof FormData>(k: K, v: FormData[K]) => void;
}> = ({ styles, label, base, data, set }) => {
  const k = (suf: string) =>
    `${base}${suf.charAt(0).toUpperCase()}${suf.slice(1)}` as keyof FormData;
  const labels: Array<[string, string]> = [
    ["vorletzt", "Vorletztes Jahr"],
    ["letzt", "Letztes Jahr"],
    ["laufend", "Laufendes Jahr"],
    ["planung", "Planung folgendes"],
  ];
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 6, color: BRAND.ink }}>
        {label}
      </div>
      <div className={styles.grid}>
        {labels.map(([suf, lbl]) => (
          <Field key={suf} styles={styles} label={lbl}>
            <input
              className={styles.input}
              value={(data[k(suf)] as string) ?? ""}
              onChange={(e) => set(k(suf), e.target.value as any)}
              placeholder="0"
            />
          </Field>
        ))}
      </div>
    </div>
  );
};

const ExternBlock: React.FC<{
  styles: ReturnType<typeof useStyles>;
  label: string;
  ynKey: keyof FormData;
  countKey: keyof FormData;
  data: FormData;
  set: <K extends keyof FormData>(k: K, v: FormData[K]) => void;
}> = ({ styles, label, ynKey, countKey, data, set }) => (
  <Field styles={styles} label={label}>
    <YesNo
      styles={styles}
      value={(data[ynKey] as "ja" | "nein" | "") ?? ""}
      onChange={(v) => set(ynKey, v as any)}
    />
    {data[ynKey] === "ja" && (
      <input
        className={styles.input}
        style={{ marginTop: 6 }}
        placeholder="Anzahl / regelmäßig?"
        value={(data[countKey] as string) ?? ""}
        onChange={(e) => set(countKey, e.target.value as any)}
      />
    )}
  </Field>
);

export default PublicCustomerRisikoanalyseKurzScrollForm;
